//@ts-nocheck
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  deleteClinician,
  getClinicianDeleteEligibility,
} from "../../../api/services/api";

type Props = {
  open: boolean;
  clinician: any;
  onClose: () => void;
};

const DeleteClinicianDialog = ({ open, clinician, onClose }: Props) => {
  const queryClient = useQueryClient();
  const clinicianId = clinician?.id;

  const { data, isLoading, isFetching, refetch } = useQuery(
    ["clinician-delete-eligibility", clinicianId],
    () => getClinicianDeleteEligibility(clinicianId),
    {
      enabled: open && !!clinicianId,
      refetchOnWindowFocus: false,
    }
  );

  // unwrapResponse keeps axios shape: { data: payload }
  const eligibility = data?.data ?? data;
  const checks = eligibility?.checks || [];
  const canDelete = Boolean(eligibility?.canDelete);
  const name =
    eligibility?.clinicianName ||
    clinician?.users?.fullName ||
    clinician?.user?.fullName ||
    "this clinician";

  const deleteMutation = useMutation(() => deleteClinician(clinicianId), {
    onSuccess: () => {
      toast.success("Clinician deleted successfully");
      queryClient.invalidateQueries(["therapists"]);
      onClose();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to delete clinician";
      toast.error(
        typeof message === "string" ? message : "Failed to delete clinician"
      );
      refetch();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete clinician</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Before deleting <strong>{name}</strong>, all pending work must be
          cleared.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Delete is only allowed when sessions, payouts, and support tickets are
          complete.
        </Typography>

        {isLoading || isFetching ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <List disablePadding>
              {checks.map((check) => {
                const blocked = check.count > 0;
                return (
                  <Box key={check.key} sx={{ mb: 1.5 }}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        {blocked ? (
                          <ErrorOutlineIcon color="error" />
                        ) : (
                          <CheckCircleOutlineIcon color="success" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            color={blocked ? "error.main" : "success.main"}
                          >
                            {check.label}
                            {blocked ? ` — ${check.count} pending` : " — clear"}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {check.description}
                          </Typography>
                        }
                      />
                    </ListItem>

                    {blocked && check.breakdown && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ pl: 4.5, display: "block", mb: 0.5 }}
                      >
                        {Object.entries(check.breakdown)
                          .map(([status, count]) => `${status}: ${count}`)
                          .join(" · ")}
                      </Typography>
                    )}

                    {blocked && check.items?.length > 0 && (
                      <Box sx={{ pl: 4.5, mb: 1 }}>
                        {check.items.slice(0, 5).map((item) => (
                          <Typography
                            key={`${check.key}-${item.id}`}
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            #{item.id}
                            {item.status ? ` · ${item.status}` : ""}
                            {item.amount != null
                              ? ` · ${item.amount} ${item.currency || ""}`
                              : ""}
                          </Typography>
                        ))}
                        {check.items.length > 5 && (
                          <Typography variant="caption" color="text.secondary">
                            +{check.items.length - 5} more
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Divider />
                  </Box>
                );
              })}
            </List>

            {!canDelete ? (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Resolve the items above, then refresh this checklist. Delete
                stays disabled until everything is clear.
              </Typography>
            ) : (
              <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                All clear. You can permanently delete this clinician.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleteMutation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={() => refetch()}
          disabled={isLoading || isFetching || deleteMutation.isLoading}
        >
          Refresh
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={!canDelete || isLoading || deleteMutation.isLoading}
          onClick={() => deleteMutation.mutate()}
        >
          {deleteMutation.isLoading ? "Deleting..." : "Delete permanently"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteClinicianDialog;
